/** One item of the blok's product list, as the canvas hands it over. */
export interface RecommenderProduct {
  /** The package it describes, e.g. `ATT_TV_MYSPORTS`. The engine matches it to a card. */
  pack_code?: string;
  /** The "See all {n}+ channels" number. Empty, the link reads without one. */
  channel_count?: string | number;
  /** Opened in the on-page modal. Empty, the blok's own link, if any. */
  view_all_url?: string;
  /** One hex color, drawn as the card's glow. */
  background_color?: string;
  /** Where this card's call to action goes. Empty, the blok's own. */
  cta_url?: string;
  /** Up to four call signs for the logo row, comma or line separated, in order. A call sign may hold a space ("MS NOW"). */
  channel_call_signs?: string;
  /** Where this card's legal link goes. Empty, the blok's own. */
  legal_link_url?: string;
}

/** A product item, read. Kept in authored order: the engine names an item by its position. */
export interface ProductSettings {
  code: string;
  channelCount: number | null;
  viewAllUrl: string;
  glowColor: string;
  ctaUrl: string;
  callSigns: string[];
  legalUrl: string;
}

export const MAX_PINNED_CHANNELS = 4;

const HEX_COLOR = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;
const COUNT = /^\d+$/;

const text = (value: unknown): string => (typeof value === "string" ? value.trim() : "");

function count(value: unknown): number | null {
  const raw = typeof value === "number" ? String(value) : text(value);
  const n = COUNT.test(raw) ? Number(raw) : 0;
  return n > 0 ? n : null;
}

/** The product list as authored. A single item may arrive unwrapped, as one object. */
export function readProducts(value: unknown): ProductSettings[] {
  const items = Array.isArray(value) ? value : value && typeof value === "object" ? [value] : [];
  return items
    .filter((item): item is RecommenderProduct => !!item && typeof item === "object" && text(item.pack_code) !== "")
    .map(item => {
      const color = text(item.background_color);
      return {
        code: text(item.pack_code),
        channelCount: count(item.channel_count),
        viewAllUrl: text(item.view_all_url),
        glowColor: HEX_COLOR.test(color) ? color : "",
        ctaUrl: text(item.cta_url),
        callSigns: text(item.channel_call_signs)
          .split(/[,\n]/)
          .map(sign => sign.trim())
          .filter((sign, at, all) => sign !== "" && all.findIndex(other => other.toUpperCase() === sign.toUpperCase()) === at)
          .slice(0, MAX_PINNED_CHANNELS),
        legalUrl: text(item.legal_link_url)
      };
    });
}
